import classNames from 'classnames';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {defineMessages, intlShape, injectIntl} from 'react-intl';
import {setProjectTitle} from '../../reducers/project-title';
import ProjectService from '../../lib/project-service';

import BufferedInputHOC from '../forms/buffered-input-hoc.jsx';
import Input from '../forms/input.jsx';
const BufferedInput = BufferedInputHOC(Input);

import styles from './project-title-input.css';

const messages = defineMessages({
    projectTitlePlaceholder: {
        id: 'gui.gui.projectTitlePlaceholder',
        description: 'Placeholder for project title when blank',
        defaultMessage: 'Project title here'
    }
});

const ProjectTitleInput = ({
    className,
    intl,
    onSubmit,
    projectTitle
}) => {
    const [error, setError] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const [projectTitles, setProjectTitles] = useState([]);

    // Fetch project titles when component mounts or projects change
    useEffect(() => {
        const fetchProjectTitles = async () => {
            if (!localStorage.getItem('accessToken')) return;
            try {
                const titles = await ProjectService.getUserProjectTitles();
                setProjectTitles(titles);
            } catch (error) {
                console.error('Failed to fetch project titles:', error);
            }
        };

        fetchProjectTitles();

        // Listen for project events to refresh project titles
        window.addEventListener('project-saved', fetchProjectTitles);
        window.addEventListener('project-loaded', fetchProjectTitles);
        window.addEventListener('login-state-changed', fetchProjectTitles);

        return () => {
            window.removeEventListener('project-saved', fetchProjectTitles);
            window.removeEventListener('project-loaded', fetchProjectTitles);
            window.removeEventListener('login-state-changed', fetchProjectTitles);
        };
    }, []);

    const validateTitle = async (title) => {
        try {
            // Basic validation
            if (!title || title.trim() === '') {
                setError('Title cannot be empty');
                return false;
            }

            const currentProjectId = localStorage.getItem('currentProjectId');
            const trimmedTitle = title.trim();

            // Check for duplicates (case-insensitive), excluding current project
            const duplicateProject = projectTitles.find(project => 
                project.title.toLowerCase() === trimmedTitle.toLowerCase() &&
                (!currentProjectId || project.id !== currentProjectId)
            );

            if (duplicateProject) {
                setError(`Cannot use "${trimmedTitle}" - This title already exists`);
                return false;
            }

            // Clear any existing errors if validation passes
            setError(null);
            return true;

        } catch (error) {
            console.error('Title validation error:', error);
            setError('Error checking title availability');
            return false;
        }
    };

    const handleSubmit = async (newTitle) => {
        setIsChecking(true);
        try {
            // Validate the title, excluding the current project
            const isValid = await validateTitle(newTitle);
            if (isValid) {
                const trimmedTitle = newTitle.trim();
                onSubmit(trimmedTitle);
                setError(null);
            }
        } catch (err) {
            console.error('Submit error:', err);
            setError('Failed to save title');
        } finally {
            setIsChecking(false);
        }
    };

    return (
        <div className={classNames(styles.titleFieldWrapper)}>
            <BufferedInput
                className={classNames(styles.titleField, className, {
                    [styles.titleFieldError]: error
                })}
                maxLength="100"
                placeholder={intl.formatMessage(messages.projectTitlePlaceholder)}
                tabIndex="0"
                type="text"
                value={projectTitle}
                onSubmit={handleSubmit}
            />
            {error && (
                <div className={styles.errorMessage}>
                    {error}
                </div>
            )}
            {isChecking && (
                <div className={styles.validating}>
                    Checking title...
                </div>
            )}
        </div>
    );
};

ProjectTitleInput.propTypes = {
    className: PropTypes.string,
    intl: intlShape.isRequired,
    onSubmit: PropTypes.func,
    projectTitle: PropTypes.string
};

const mapStateToProps = state => ({
    projectTitle: state.scratchGui.projectTitle
});

const mapDispatchToProps = dispatch => ({
    onSubmit: title => dispatch(setProjectTitle(title))
});

export default injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(ProjectTitleInput));
